import React, { useEffect, useState } from "react";
import axios from "axios";
import { fetchPurchaseOrdersAll,fetchPurchaseOrdersWithoutVendor } from "../Api/ViewPurchaseOrder";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";
import { toast } from "react-toastify";
import { fetchCompnayCodesById } from "../Api/InvoiceApi";
import "../Styling/vendorInvoice.css";

// Small helper
const todayISO = () => {
  const t = new Date();
  return t.toISOString().slice(0, 10); // YYYY-MM-DD
};

export default function VendorInvoiceScreen() {
  const { user } = useAuth();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState("");
  const [companyCodes, setCompanyCodes] = useState([]);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // New states for PO line items
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  // invoice type dropdown (kept)
  const [invoiceType, setInvoiceType] = useState("vendor-invoice");
  const invoiceTypes = [{ value: "vendor-invoice", label: "Vendor Invoice" }];

  // Vendors
  const [vendors, setVendors] = useState([]);
  const [vendorMap, setVendorMap] = useState({}); // code -> name

  // GST rates state
  const [gstData, setGstData] = useState({});

  // load purchase orders
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const result = await fetchPurchaseOrdersWithoutVendor();
        if (!mounted) return;
        setPurchaseOrders(result || []);
        if ((result || []).length > 0) {
          setSelectedPO(
            result[0].purchaseOrderNumber ??
              result[0].PurchaseOrderNumber ??
              result[0].id ??
              ""
          );
        }
      } catch (err) {
        console.error("Failed to load purchase orders", err);
        setError("Failed to load purchase orders");
      }
    };
    load();
    return () => (mounted = false);
  }, []);

  // load company codes for the user's organisation
  useEffect(() => {
    let mounted = true;
    const loadCompanyCodes = async () => {
      try {
        const orgId =
          user?.organisaionId ??
          user?.organisationId ??
          user?.organizationId ??
          user?.orgId;
        if (!orgId) return;

        const codes = await fetchCompnayCodesById(orgId);
        if (!mounted) return;
        setCompanyCodes(codes || []);
        if ((codes || []).length > 0) {
          const first = codes[0];
          const value =
            first.companyCode ?? first.CompanyCode ?? first.code ?? first.id ?? "";
          setSelectedCompanyCode(value);
        }
      } catch (err) {
        console.error("Failed to load company codes", err);
        setError((prev) => prev || "Failed to load company codes");
      }
    };

    loadCompanyCodes();
    return () => (mounted = false);
  }, [user]);

  // Load vendor master for name lookup
  useEffect(() => {
    let mounted = true;
    const loadVendors = async () => {
      try {
        const resp = await axios.get(`${Base_URL}/VendorMaster`);
        const list = Array.isArray(resp.data) ? resp.data : [];
        if (!mounted) return;
        setVendors(list);

        const map = {};
        list.forEach((v) => {
          const code =
            v.vendorCode ??
            v.code ??
            v.merchantCode ??
            v.MerchantCode ??
            v.vendor_id ??
            v.id ??
            null;
          const name =
            v.vendorName ?? v.name ?? v.companyName ?? v.VendorName ?? null;
          if (code) map[String(code)] = name ?? String(code);
        });
        if (mounted) setVendorMap(map);
      } catch (err) {
        console.error("Failed to load vendors", err);
        toast.warning("Failed to load vendor master. Showing merchant codes.");
      }
    };
    loadVendors();
    return () => (mounted = false);
  }, []);

  // Fetch GST data for a specific item
  const fetchGstDataForItem = async (itemCode) => {
    if (!itemCode) {
      return { CGST: 0, IGST: 0, SGST: 0 };
    }

    try {
      console.log("Fetching GST data for item:", itemCode);
      
      const response = await axios.get(`${Base_URL}/VendorInvoice/GetGst?itemCode=${encodeURIComponent(itemCode)}`);
      console.log("GST API Response for item", itemCode, ":", response.data);

      if (response.data) {
        const data = response.data;
        
        // Handle different response structures
        let gstRates = { 
          CGST: 0, 
          IGST: 0, 
          SGST: 0 
        };

        // Extract GST rates from the response
        if (data.CGST_Percent !== undefined) {
          gstRates = {
            CGST: parseFloat(data.CGST_Percent) || 0,
            IGST: parseFloat(data.IGST_Percent) || 0,
            SGST: parseFloat(data.SGST_Percent) || 0
          };
        }
        // If response is an array, take the first item
        else if (Array.isArray(data) && data.length > 0) {
          const firstItem = data[0];
          gstRates = {
            CGST: parseFloat(firstItem.CGST_Percent) || 0,
            IGST: parseFloat(firstItem.IGST_Percent) || 0,
            SGST: parseFloat(firstItem.SGST_Percent) || 0
          };
        }
        // Handle different property names
        else if (data.cgsT_Percent !== undefined) {
          gstRates = {
            CGST: parseFloat(data.cgsT_Percent) || 0,
            IGST: parseFloat(data.igsT_Percent) || 0,
            SGST: parseFloat(data.sgsT_Percent) || 0
          };
        }

        console.log("Final GST Rates for item", itemCode, ":", gstRates);
        return gstRates;
      }
      
      return { CGST: 0, IGST: 0, SGST: 0 };
      
    } catch (error) {
      console.error("Error fetching GST data for item", itemCode, ":", error);
      return { CGST: 0, IGST: 0, SGST: 0 };
    }
  };

  // Calculate GST amounts based on basic value and GST rates
  const calculateGstAmounts = (basicValue, cgstRate, igstRate, sgstRate) => {
    if (!basicValue) {
      return {
        cgstAmount: 0,
        igstAmount: 0,
        sgstAmount: 0,
        totalAmount: 0
      };
    }

    const amount = parseFloat(basicValue);
    const cgstAmount = (amount * (cgstRate || 0)) / 100;
    const igstAmount = (amount * (igstRate || 0)) / 100;
    const sgstAmount = (amount * (sgstRate || 0)) / 100;
    const totalAmount = amount + cgstAmount + igstAmount + sgstAmount;

    return {
      cgstAmount: parseFloat(cgstAmount.toFixed(2)),
      igstAmount: parseFloat(igstAmount.toFixed(2)),
      sgstAmount: parseFloat(sgstAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2))
    };
  };

  // fetch items when selectedPO changes
  useEffect(() => {
    if (!selectedPO) {
      setPurchaseItems([]);
      return;
    }
    fetchPurchaseItems(selectedPO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPO]);

  // helper to fetch purchase items for a selected PO number
  const fetchPurchaseItems = async (poNumber) => {
    if (!poNumber) {
      setPurchaseItems([]);
      return;
    }

    setItemsLoading(true);
    setPurchaseItems([]);

    try {
      const url = `${Base_URL}/PurchaseOrder/InventoryQuantity/id?id=${encodeURIComponent(
        poNumber
      )}`;
      const resp = await axios.get(url);
      const payload = resp?.data ?? {};

      // payload can be:
      // 1) an array where each element is { inventoryItems: {...} }
      // 2) an array of item objects
      // 3) an object { inventoryItems: {...} } or { inventoryItems: [...] }
      // 4) a single item object
      let itemsRaw = [];

      if (Array.isArray(payload)) {
        // extract inventoryItems if present, otherwise use the element itself
        itemsRaw = payload.map((el) => el?.inventoryItems ?? el).filter(Boolean);
      } else {
        // object payload
        const maybe = payload.inventoryItems ?? payload;
        itemsRaw = Array.isArray(maybe) ? maybe : maybe ? [maybe] : [];
      }

      // Fetch GST data for each item and calculate amounts
      const mapped = await Promise.all(
        itemsRaw.map(async (it) => {
          const origQty =
            parseFloat(
              it.originalQuantity ?? it.originalQty ?? it.quantity ?? it.qty ?? 0
            ) || 0;

          const quantityReceived =
            parseFloat(it.quantityReceived ?? it.inventoryQuantity ?? 0) || 0;

          const invoiceQuantity = parseFloat(it.quantityReceived ?? 0) || 0;
          const price = parseFloat(it.price ?? it.Price ?? 0) || 0;

          const balance =
            parseFloat(it.balanceQuantity ?? origQty - quantityReceived) || 0;

          const basicValue = invoiceQuantity * price;

          // Fetch GST data for this item
          const itemCode = it.itemCode ?? it.Item_code ?? it.item_code ?? it.item ?? null;
          let gstRates = { CGST: 0, IGST: 0, SGST: 0 };
          
          if (itemCode) {
            gstRates = await fetchGstDataForItem(itemCode);
          }

          const gstAmounts = calculateGstAmounts(
            basicValue,
            gstRates.CGST,
            gstRates.IGST,
            gstRates.SGST
          );

          return {
            id: it.id ?? it.ID ?? it.Id ?? null,
            raw: it,
            item_code: itemCode,
            description: it.Description ?? it.description ?? it.desc ?? "",
            originalQuantity: origQty,
            unit: it.Unit ?? it.unit ?? it.uom ?? "",
            price,
            requestedDeliveryDate:
              it.Requested_Delivery_Date ??
              it.requested_delivery_date ??
              it.requestedDeliveryDate ??
              null,

            quantityReceived,
            invoiceQuantity,
            balance,
            basicValue,
            receiptDate: it.receiptDate ?? it.ReceiptDate ?? todayISO(),
            remarks: it.remarks ?? it.Remark ?? "",
            
            // GST fields
            cgstRate: gstRates.CGST,
            igstRate: gstRates.IGST,
            sgstRate: gstRates.SGST,
            cgstAmount: gstAmounts.cgstAmount,
            igstAmount: gstAmounts.igstAmount,
            sgstAmount: gstAmounts.sgstAmount,
            totalAmount: gstAmounts.totalAmount
          };
        })
      );

      console.log("mapped purchase items with GST:", mapped);
      setPurchaseItems(mapped);
    } catch (err) {
      console.error("Failed to load purchase items", err);
      toast.error("Failed to load purchase items");
    } finally {
      setItemsLoading(false);
    }
  };

  // Recalculate GST when invoice quantity changes
  const recalculateGstForItem = (item) => {
    const basicValue = (item.invoiceQuantity || 0) * (item.price || 0);
    const gstAmounts = calculateGstAmounts(
      basicValue,
      item.cgstRate || 0,
      item.igstRate || 0,
      item.sgstRate || 0
    );

    return {
      ...item,
      basicValue,
      cgstAmount: gstAmounts.cgstAmount,
      igstAmount: gstAmounts.igstAmount,
      sgstAmount: gstAmounts.sgstAmount,
      totalAmount: gstAmounts.totalAmount
    };
  };

  // Editable field handlers for each line
  const handleQuantityReceivedChange = (index, value) => {
    setPurchaseItems((prev) => {
      const copy = [...prev];
      const num = value === "" ? "" : parseFloat(value);
      const orig = copy[index].originalQuantity ?? 0;

      if (num === "") {
        copy[index].quantityReceived = "";
      } else if (isNaN(num)) {
        return prev;
      } else {
        if (num > orig) {
          toast.warning(
            `Quantity received cannot exceed original quantity (${orig}). Setting to original.`
          );
          copy[index].quantityReceived = orig;
        } else if (num < 0) {
          copy[index].quantityReceived = 0;
        } else {
          copy[index].quantityReceived = num;
        }
      }

      // ensure invoiceQuantity doesn't exceed quantityReceived
      const qRecv =
        copy[index].quantityReceived === "" ? 0 : Number(copy[index].quantityReceived);
      if (copy[index].invoiceQuantity > qRecv) {
        toast.warning(
          "Invoice quantity cannot exceed received quantity. Adjusting invoice quantity."
        );
        copy[index].invoiceQuantity = qRecv;
      }

      // recalc balance
      const invoiceQty = Number(copy[index].invoiceQuantity || 0);
      const balance = Number(copy[index].quantityReceived || 0) - invoiceQty;
      copy[index].balance = isNaN(balance) ? "" : balance;

      // Recalculate GST
      copy[index] = recalculateGstForItem(copy[index]);

      return copy;
    });
  };

  const handleInvoiceQuantityChange = (index, value) => {
    setPurchaseItems((prev) => {
      const copy = [...prev];
      const num = value === "" ? "" : parseFloat(value);
      const qRecv = Number(copy[index].quantityReceived || 0);
      const price = Number(copy[index].price || 0);

      if (num === "") {
        copy[index].invoiceQuantity = "";
      } else if (isNaN(num)) {
        return prev;
      } else {
        if (num < 0) {
          copy[index].invoiceQuantity = 0;
        } else if (num > qRecv) {
          toast.warning(
            "Invoice quantity cannot exceed received quantity. Setting to received quantity."
          );
          copy[index].invoiceQuantity = qRecv;
        } else {
          copy[index].invoiceQuantity = num;
        }
      }

      // recalc balance
      const invoiceQty = Number(copy[index].invoiceQuantity || 0);
      const balance = qRecv - invoiceQty;
      copy[index].balance = isNaN(balance) ? "" : balance;

      // Recalculate GST
      copy[index] = recalculateGstForItem(copy[index]);

      return copy;
    });
  };

  const handleReceiptDateChange = (index, value) => {
    const max = todayISO();
    if (value > max) {
      toast.warning("Receipt date cannot be in the future. Setting to today.");
      value = max;
    }
    setPurchaseItems((prev) => {
      const copy = [...prev];
      copy[index].receiptDate = value;
      return copy;
    });
  };

  const handleRemarksChange = (index, value) => {
    setPurchaseItems((prev) => {
      const copy = [...prev];
      copy[index].remarks = value;
      return copy;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (!selectedPO) {
      setError("Please select a Purchase Order.");
      return;
    }

    if (!selectedCompanyCode) {
      setError("Please select a Company Code.");
      return;
    }

    if (!user || !user.userId) {
      setError("User not found or not authenticated.");
      return;
    }

    // validate lines
    for (let i = 0; i < purchaseItems.length; i++) {
      const it = purchaseItems[i];

      if (
        it.quantityReceived === "" ||
        it.quantityReceived == null ||
        isNaN(Number(it.quantityReceived))
      ) {
        setError(
          `Quantity Received required and must be a number for line ${i + 1}.`
        );
        return;
      }

      if (
        it.invoiceQuantity === "" ||
        it.invoiceQuantity == null ||
        isNaN(Number(it.invoiceQuantity))
      ) {
        setError(
          `Invoice Quantity required and must be a number for line ${i + 1}.`
        );
        return;
      }

      if (Number(it.invoiceQuantity) > Number(it.quantityReceived)) {
        setError(
          `Invoice Quantity cannot exceed Quantity Received for line ${i + 1}.`
        );
        return;
      }
    }

    const payload = {
      purchaseOrder: selectedPO,
      createdBy: String(user.userId),
      invoiceType,
      companyCode: String(selectedCompanyCode),
      items: purchaseItems.map((it) => ({
        item_code: it.item_code,
        description: it.description,
        originalQuantity: it.originalQuantity,
        quantityReceived: it.quantityReceived,
        invoiceQuantity: it.invoiceQuantity,
        balance: it.balance,
        unit: it.unit,
        price: it.price,
        requestedDeliveryDate: it.requestedDeliveryDate,
        receiptDate: it.receiptDate,
        remarks: it.remarks,
        cgstRate: it.cgstRate,
        igstRate: it.igstRate,
        sgstRate: it.sgstRate,
        cgstAmount: it.cgstAmount,
        igstAmount: it.igstAmount,
        sgstAmount: it.sgstAmount,
        totalAmount: it.totalAmount
      })),
    };
    console.log(payload);
    try {
      setIsSubmitting(true);
      const resp = await axios.post(`${Base_URL}/VendorInvoice`, payload);

      if (resp && resp.data) {
        toast.success(`${resp.data.vendorInvoiceNumber} Invoice Created Successfully`);
        setMessage(
          resp.data.vendorInvoiceNumber
            ? `Created: ${resp.data.vendorInvoiceNumber}`
            : "Vendor invoice created successfully."
        );
      } else {
        toast.error("Error creating invoice");
        setMessage("Vendor invoice created successfully.");
      }
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data || err.message || "Failed to create vendor invoice.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setIsSubmitting(false);
    }
  };

  // helper to resolve vendor name from merchant code (fallback to code)
  const resolveVendorName = (merchantCode) => {
    if (!merchantCode) return "";
    return vendorMap[String(merchantCode)] ?? merchantCode;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 mx">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="px-8 py-6 border-b">
            <h2 className="text-2xl font-semibold mx-3 mt-3">Create Vendor Invoice</h2>
          </div>

          {/* Header: Purchase Order, Company Code, Invoice Type, Created By on one responsive line */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="vendorInvoiceHeader">
              <div>
                <label className="block text-sm font-medium mb-1">Select Purchase Order</label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  value={selectedPO}
                  onChange={(e) => setSelectedPO(e.target.value)}
                  disabled={isSubmitting || purchaseOrders.length === 0}
                >
                  {purchaseOrders.length === 0 && <option value="">-- No purchase orders --</option>}
                  {purchaseOrders.map((po, idx) => {
                    const value = po.purchaseOrderNumber ?? po.PurchaseOrderNumber ?? po.id ?? po.purchaseOrder ?? "";
                    const poDate = po.pO_Date ? String(po.pO_Date).split("T")[0] : "";
                    const merchant = po.merchantCode ?? po.merchant ?? po.supplierCode ?? po.supplier ?? "";
                    const vendorName = resolveVendorName(merchant);
                    return (
                      <option key={idx} value={value}>
                        {`${value}${vendorName ? ` - ${vendorName}` : merchant ? ` - ${merchant}` : ""}${poDate ? ` - ${poDate}` : ""}`}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Company Code</label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  value={selectedCompanyCode}
                  onChange={(e) => setSelectedCompanyCode(e.target.value)}
                  disabled={isSubmitting || companyCodes.length === 0}
                >
                  {companyCodes.length === 0 && <option value="">-- No company codes --</option>}
                  {companyCodes.map((c, idx) => {
                    const value = c.companyCode ?? c.CompanyCode ?? c.code ?? c.id ?? "";
                    const label = c.companyName ?? c.CompanyName ?? c.name ?? value;
                    return (
                      <option key={idx} value={value}>
                        {label} {value ? `(${value})` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Invoice Type</label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  value={invoiceType}
                  onChange={(e) => setInvoiceType(e.target.value)}
                  disabled={isSubmitting}
                >
                  {invoiceTypes.map((it) => (
                    <option key={it.value} value={it.value}>
                      {it.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Created By</label>
                <input
                  className="w-full border rounded px-3 py-2 bg-gray-50 text-sm"
                  value={`${user?.userId ?? ""} - ${user?.username ?? ""}`}
                  readOnly
                />
              </div>
            </div>
          </form>

          {/* Line items */}
          <div className="p-6">
            <h3 className="text-xl font-semibold mb-4">Purchase Order Line Items</h3>

            {itemsLoading ? (
              <div className="py-4 text-center">Loading items...</div>
            ) : purchaseItems.length === 0 ? (
              <div className="text-gray-600">No purchase items to display.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border">
                  <thead>
                    <tr className="bg-gray-100 text-left">
                      <th className="px-3 py-2 border">#</th>
                      <th className="px-3 py-2 border">Item Code</th>
                      <th className="px-3 py-2 border">Description</th>
                      <th className="px-3 py-2 border">Original Qty</th>
                      <th className="px-3 py-2 border">Qty Received</th>
                      <th className="px-3 py-2 border">Invoice Qty</th>
                      <th className="px-3 py-2 border">Balance</th>
                      <th className="px-3 py-2 border">Unit</th>
                      <th className="px-3 py-2 border">Price</th>
                      <th className="px-3 py-2 border">Basic Value</th>
                      
                      {/* GST Headers */}
                      <th className="px-3 py-2 border bg-blue-50">CGST %</th>
                      <th className="px-3 py-2 border bg-blue-50">CGST Amount</th>
                      <th className="px-3 py-2 border bg-green-50">SGST %</th>
                      <th className="px-3 py-2 border bg-green-50">SGST Amount</th>
                      <th className="px-3 py-2 border bg-yellow-50">IGST %</th>
                      <th className="px-3 py-2 border bg-yellow-50">IGST Amount</th>
                      <th className="px-3 py-2 border bg-gray-100">Total Amount</th>
                      
                      <th className="px-3 py-2 border">Requested Delivery</th>
                      <th className="px-3 py-2 border">Receipt Date</th>
                      <th className="px-3 py-2 border">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseItems.map((it, idx) => (
                      <tr key={it.id ?? idx} className="odd:bg-white even:bg-gray-50">
                        <td className="px-3 py-2 border align-top">{idx + 1}</td>
                        <td className="px-3 py-2 border align-top">{it.item_code}</td>
                        <td className="px-3 py-2 border align-top">{it.description}</td>
                        <td className="px-3 py-2 border align-top">{it.originalQuantity}</td>
                        <td className="px-3 py-2 border align-top" style={{ minWidth: 140 }}>
                          <input
                            type="number"
                            step="1"
                            min={0}
                            max={it.originalQuantity}
                            value={it.quantityReceived}
                            disabled
                            onChange={(e) => handleQuantityReceivedChange(idx, e.target.value)}
                            className="w-full border rounded px-2 py-1"
                          />
                        </td>

                        <td className="px-3 py-2 border align-top" style={{ minWidth: 140 }}>
                          <input
                            type="number"
                            step="1"
                            min={0}
                            max={it.quantityReceived}
                            value={it.invoiceQuantity}
                            onChange={(e) => handleInvoiceQuantityChange(idx, e.target.value)}
                            className="w-full border rounded px-2 py-1"
                          />
                        </td>

                        <td className="px-3 py-2 border align-top">{it.balance}</td>

                        <td className="px-3 py-2 border align-top">{it.unit}</td>
                        <td className="px-3 py-2 border align-top">{it.price}</td>
                        <td className="px-3 py-2 border align-top font-medium">
                          {it.basicValue ? it.basicValue.toFixed(2) : "0.00"}
                        </td>
                        
                        {/* GST Fields */}
                        <td className="px-3 py-2 border align-top bg-blue-50">
                          {it.cgstRate?.toFixed(2)}%
                        </td>
                        <td className="px-3 py-2 border align-top bg-blue-50">
                          {it.cgstAmount?.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 border align-top bg-green-50">
                          {it.sgstRate?.toFixed(2)}%
                        </td>
                        <td className="px-3 py-2 border align-top bg-green-50">
                          {it.sgstAmount?.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 border align-top bg-yellow-50">
                          {it.igstRate?.toFixed(2)}%
                        </td>
                        <td className="px-3 py-2 border align-top bg-yellow-50">
                          {it.igstAmount?.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 border align-top font-bold bg-gray-100">
                          {it.totalAmount?.toFixed(2)}
                        </td>
                        
                        <td className="px-3 py-2 border align-top">{it.requestedDeliveryDate ? new Date(it.requestedDeliveryDate).toLocaleDateString() : ""}</td>
                        <td className="px-3 py-2 border align-top" style={{ minWidth: 140 }}>
                          <input
                            type="date"
                            value={it.receiptDate}
                            max={todayISO()}
                            onChange={(e) => handleReceiptDateChange(idx, e.target.value)}
                            className="w-full border rounded px-2 py-1"
                          />
                        </td>
                        <td className="px-3 py-2 border align-top">
                          <input
                            type="text"
                            value={it.remarks}
                            onChange={(e) => handleRemarksChange(idx, e.target.value)}
                            className="w-full border rounded px-2 py-1"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-semibold">
                      <td colSpan="9" className="px-3 py-2 border text-right">Total Basic Value:</td>
                      <td className="px-3 py-2 border">
                        {purchaseItems
                          .reduce((sum, it) => sum + (Number(it.basicValue) || 0), 0)
                          .toFixed(2)}
                      </td>
                      
                      {/* GST Totals */}
                      <td colSpan="2" className="px-3 py-2 border bg-blue-50 text-right">
                        Total CGST:
                      </td>
                      <td className="px-3 py-2 border bg-blue-50">
                        {purchaseItems
                          .reduce((sum, it) => sum + (Number(it.cgstAmount) || 0), 0)
                          .toFixed(2)}
                      </td>
                      
                      <td colSpan="2" className="px-3 py-2 border bg-green-50 text-right">
                        Total SGST:
                      </td>
                      <td className="px-3 py-2 border bg-green-50">
                        {purchaseItems
                          .reduce((sum, it) => sum + (Number(it.sgstAmount) || 0), 0)
                          .toFixed(2)}
                      </td>
                      
                      <td colSpan="2" className="px-3 py-2 border bg-yellow-50 text-right">
                        Total IGST:
                      </td>
                      <td className="px-3 py-2 border bg-yellow-50">
                        {purchaseItems
                          .reduce((sum, it) => sum + (Number(it.igstAmount) || 0), 0)
                          .toFixed(2)}
                      </td>
                      
                      <td className="px-3 py-2 border bg-gray-200 font-bold">
                        {purchaseItems
                          .reduce((sum, it) => sum + (Number(it.totalAmount) || 0), 0)
                          .toFixed(2)}
                      </td>
                      
                      <td colSpan="3" className="px-3 py-2 border"></td>
                    </tr>
                  </tfoot>
                </table>

                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setPurchaseItems((prev) =>
                        prev.map((it) => ({
                          ...it,
                          quantityReceived: it.originalQuantity,
                          invoiceQuantity: 0,
                          balance: it.originalQuantity - 0,
                          receiptDate: todayISO(),
                          remarks: "",
                        }))
                      )
                    }
                    className="px-4 py-2 rounded border bg-gray-50"
                  >
                    Reset Lines
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Submit button moved to bottom */}
          <div className="p-6 border-t bg-white">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {message && <div className="text-green-600">{message}</div>}
                {error && <div className="text-red-600">{error}</div>}
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`px-6 py-2 rounded text-white ${isSubmitting ? "bg-gray-400" : "bg-blue-600 hover:opacity-90"}`}
                >
                  {isSubmitting ? "Creating..." : "Create Vendor Invoice"}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}